/*
  InvoiceSendDialog.jsx — Send an invoice via Email or WhatsApp.
  Extracted from InvoicesPanel.jsx (Feb 2026 refactor).
*/
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send, Loader2, Mail, MessageCircle, FileText } from "lucide-react";

export default function InvoiceSendDialog({
  open, onOpenChange, sendTarget, sendForm, setSendForm, sending, doSend,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" data-testid="send-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="w-4 h-4 text-[#0066CC]" /> Send Invoice</DialogTitle>
          <DialogDescription className="text-xs">
            Share <strong className="font-mono">{sendTarget?.invoice_no || "—"}</strong> with the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="inline-flex rounded-lg border overflow-hidden" data-testid="send-channel-toggle">
            <button onClick={() => setSendForm(f => ({ ...f, channel: "email" }))} className={`px-4 py-1.5 text-xs font-semibold gap-1 flex items-center ${sendForm.channel === "email" ? "bg-[#0066CC] text-white" : "bg-white text-slate-600"}`} data-testid="channel-email"><Mail className="w-3.5 h-3.5" /> Email</button>
            <button onClick={() => setSendForm(f => ({ ...f, channel: "whatsapp" }))} className={`px-4 py-1.5 text-xs font-semibold gap-1 flex items-center ${sendForm.channel === "whatsapp" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`} data-testid="channel-whatsapp"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
          </div>

          {sendForm.channel === "email" ? (
            <>
              <div>
                <Label className="text-xs">To (Email) *</Label>
                <Input type="email" value={sendForm.to_email} onChange={e => setSendForm({ ...sendForm, to_email: e.target.value })} placeholder="accounts@client.com" className="h-9 text-xs" data-testid="send-to-email" />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={sendForm.subject} onChange={e => setSendForm({ ...sendForm, subject: e.target.value })} className="h-9 text-xs" data-testid="send-subject" />
              </div>
              <div>
                <Label className="text-xs">Message</Label>
                <Textarea value={sendForm.message} onChange={e => setSendForm({ ...sendForm, message: e.target.value })} rows={4} className="text-xs" data-testid="send-message" />
              </div>
              <div className="p-2 rounded bg-blue-50 text-[11px] text-blue-800 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Invoice PDF will be attached automatically.
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs">WhatsApp Phone * <span className="text-slate-400">(country + number, no +)</span></Label>
                <Input value={sendForm.to_phone} onChange={e => setSendForm({ ...sendForm, to_phone: e.target.value.replace(/\s|\+/g, "") })} placeholder="919876543210" className="h-9 text-xs" data-testid="send-to-phone" />
              </div>
              <div>
                <Label className="text-xs">Approved Template Name *</Label>
                <Input value={sendForm.whatsapp_template_name || ""} onChange={e => setSendForm({ ...sendForm, whatsapp_template_name: e.target.value })} placeholder="invoice_share (from Meta WABA)" className="h-9 text-xs" data-testid="send-wa-template" />
                <p className="text-[10px] text-slate-500 mt-1">Template variables (in order): customer_name, invoice_no, amount, invoice_date.</p>
              </div>
              <div className="p-2 rounded bg-amber-50 text-[11px] text-amber-800">
                WhatsApp Business Cloud API requires an approved template. Configure credentials in Settings → Reminders → WhatsApp.
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancel</Button>
            <Button onClick={doSend} disabled={sending} className={sendForm.channel === "email" ? "bg-[#0066CC] hover:bg-[#0052A3]" : "bg-emerald-600 hover:bg-emerald-700"} data-testid="confirm-send-btn">
              {sending ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Sending…</> : <><Send className="w-4 h-4 mr-1" /> Send</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
